import { createState } from './initial';
import { clearCoveredPending, sourceStatus, startRound, updateMembership } from './membership';
import { commitResponse } from './response';
import { afterEviction, classifyRows, leadDue, pruneMetadata, sameSymbols, symbolSnapshot } from './rows';
import { automaticClasses, isActivated, leadEligible, liveEligible, roundReady } from './selectors';
export function currentTicket(state, ticket) {
    return (state.fetch.status === 'in-flight' &&
        state.fetch.ticket.entityId === ticket.entityId &&
        state.fetch.ticket.generation === ticket.generation &&
        state.fetch.ticket.requestId === ticket.requestId);
}
function claimIntent(state, intent) {
    const instruction = Object.freeze({ ...intent.instruction, symbols: symbolSnapshot(intent.instruction.symbols) });
    let next = {
        ...state,
        fetch: {
            status: 'in-flight',
            instruction,
            ticket: Object.freeze({
                entityId: state.entityId,
                generation: state.source.generation,
                requestId: state.nextRequestId,
            }),
        },
        nextRequestId: state.nextRequestId + 1,
    };
    if (intent.automaticClass !== null) {
        next = {
            ...next,
            nextAutomaticClass: automaticClasses[(automaticClasses.indexOf(intent.automaticClass) + 1) % automaticClasses.length],
        };
    }
    if (instruction.kind === 'up' && instruction.cause === 'manual')
        next = { ...next, manualUp: false };
    if ((instruction.kind === 'up' && instruction.cause === 'live') ||
        (instruction.kind === 'initial' && intent.automaticClass === 'upward')) {
        next = { ...next, liveDue: false };
    }
    if (instruction.kind === 'backfill' && next.backfill.status === 'none') {
        next = {
            ...next,
            backfill: {
                status: 'paging',
                entries: next.pending.filter((entry) => instruction.symbols.includes(entry.symbol)),
                newestCursor: instruction.newestCursor,
                oldestCursor: instruction.oldestCursor,
                page: instruction.page,
                tail: null,
            },
        };
    }
    if (instruction.kind === 'lead' && next.lead.refresh.status !== 'paging') {
        next = {
            ...next,
            lead: {
                ...next.lead,
                refresh: {
                    status: 'paging',
                    symbols: instruction.symbols,
                    page: instruction.page,
                    tail: null,
                    staging: [],
                    refreshQueued: false,
                },
            },
        };
    }
    return next;
}
export function reducer(state, action, adapter, config) {
    if (state.disposed)
        return state;
    switch (action.type) {
        case 'beginSource':
        case 'activateSource': {
            const fresh = createState(state.entityId, action.source);
            let next = {
                ...fresh,
                source: { ...fresh.source, generation: state.source.generation + 1 },
                nextRequestId: state.nextRequestId,
                nextToken: state.nextToken,
                measurement: { ...state.measurement, scrollTop: 0, heights: new Map() },
            };
            if (action.type === 'activateSource') {
                next = startRound({
                    ...next,
                    source: { ...next.source, status: 'active' },
                    activeSymbols: symbolSnapshot(action.symbols),
                }, [], false);
            }
            return next;
        }
        case 'membershipReset':
            return pruneMetadata({
                ...state,
                source: { ...state.source, status: 'awaiting-symbols' },
                resetCoverage: state.source.status === 'awaiting-symbols' ? state.resetCoverage : state.roundSymbols,
                roundSymbols: [],
                fetch: { status: 'idle' },
                backfill: { status: 'none' },
                manualUp: false,
                liveDue: false,
                lead: {
                    ...state.lead,
                    refresh: state.lead.refresh.status === 'paging' ? { status: 'due' } : state.lead.refresh,
                },
            }, adapter);
        case 'activateSymbols': {
            if (state.source.status !== 'awaiting-symbols')
                return state;
            const next = updateMembership(state, action.symbols, action.now, adapter, config);
            const activated = startRound({
                ...next,
                source: { ...next.source, status: 'active' },
                pending: state.pending.filter((entry) => action.symbols.includes(entry.symbol)),
                resetCoverage: [],
            }, state.resetCoverage, liveEligible({ ...state, source: { ...state.source, status: 'active' } }, config));
            return sourceStatus(activated);
        }
        case 'publishSymbols':
            if (state.source.status === 'awaiting-symbols' || state.source.status === 'awaiting-table-reset') {
                return state;
            }
            return updateMembership(state, action.symbols, action.now, adapter, config);
        case 'cleanup':
            if (state.unmatchedAt.size === 0 ||
                ![...state.unmatchedAt.values()].some((time) => action.now - time >= state.source.retention)) {
                return state;
            }
            return sourceStatus(afterEviction(state, classifyRows(state, action.now, true, adapter), adapter, config));
        case 'setRetention': {
            if (state.source.retention === action.retention)
                return state;
            const next = classifyRows({ ...state, source: { ...state.source, retention: action.retention } }, action.now, true, adapter);
            return sourceStatus(afterEviction(state, next, adapter, config));
        }
        case 'measure': {
            const old = state.measurement;
            const incoming = action.measurement;
            if (old.scrollTop === incoming.scrollTop &&
                old.viewportHeight === incoming.viewportHeight &&
                old.heights.size === incoming.heights.size &&
                [...old.heights].every(([key, height]) => incoming.heights.get(key) === height)) {
                return state;
            }
            const next = {
                ...state,
                measurement: action.measurement,
                liveTopRecovery: state.liveTopRecovery && incoming.scrollTop <= config.pinnedTolerance,
            };
            return liveEligible(next, config) ? next : { ...next, liveDue: false };
        }
        case 'trim': {
            if (action.start === 0 && action.end === state.rows.length - 1)
                return state;
            const next = classifyRows({ ...state, rows: state.rows.slice(action.start, action.end + 1) }, action.now, false, adapter);
            return sourceStatus(afterEviction(state, next, adapter, config));
        }
        case 'reconcileRound':
            if (!roundReady(state, adapter, config))
                return state;
            return sameSymbols(state.activeSymbols, state.roundSymbols)
                ? sourceStatus(state)
                : startRound(state, state.roundSymbols, liveEligible(state, config));
        case 'claim':
            if (state.fetch.status !== 'idle' ||
                !isActivated(state) ||
                state.roundSymbols.length === 0 ||
                state.measurement.viewportHeight <= 0) {
                return state;
            }
            return claimIntent(state, action.intent);
        case 'resolve':
            if (!currentTicket(state, action.ticket) || state.fetch.status !== 'in-flight')
                return state;
            return commitResponse({ ...state, fetch: state.fetch }, action.result, action.now, adapter);
        case 'reject':
            if (!currentTicket(state, action.ticket) || state.fetch.status !== 'in-flight')
                return state;
            return { ...state, fetch: { status: 'failed', instruction: state.fetch.instruction, error: action.error } };
        case 'retry':
            if (state.fetch.status !== 'failed' || !isActivated(state) || state.measurement.viewportHeight <= 0) {
                return state;
            }
            return claimIntent(state, { instruction: state.fetch.instruction, automaticClass: null });
        case 'manualUp':
            return state.manualUp ? state : { ...state, manualUp: true };
        case 'liveTick':
            return liveEligible(state, config) && !state.liveDue ? { ...state, liveDue: true } : state;
        case 'leadTick':
            return leadEligible(state, adapter) ? leadDue(state) : state;
        case 'resetCurrentSource': {
            const fresh = createState(state.entityId, state.source);
            const status = state.source.status === 'awaiting-table-reset' || state.source.status === 'awaiting-symbols'
                ? state.source.status
                : 'active';
            const next = {
                ...fresh,
                source: { ...state.source, status },
                activeSymbols: state.activeSymbols,
                nextRequestId: state.nextRequestId,
                nextToken: state.nextToken,
                measurement: { ...state.measurement, scrollTop: 0, heights: new Map() },
            };
            return clearCoveredPending(startRound(next, [], false), state.activeSymbols);
        }
        case 'dispose':
            return {
                ...state,
                disposed: true,
                fetch: { status: 'idle' },
                rows: [],
                unmatchedAt: new Map(),
                parity: new Map(),
                pagedRowKeys: new Set(),
                pending: [],
                activeSymbols: [],
                roundSymbols: [],
                resetCoverage: [],
                backfill: { status: 'none' },
                lead: { rows: [], refresh: { status: 'current' } },
                liveDue: false,
                liveCatchUp: 'inactive',
                liveTopRecovery: false,
                manualUp: false,
            };
    }
}
//# sourceMappingURL=reducer.js.map